import { REDACT_CATEGORIES, type Stop } from './demos'

// Code panel snippets (spec §5). One body builder per demo; three serializers.
// Adapted from rakerone-web/src/lib/v1-snippets.ts (curl heredoc form) with the
// paperwork CLI and @cloudraker/api SDK emitters added.
import type { DemoParams } from '@/shared/contract'

export const API = 'https://api.cloudraker.com'
export type Lang = 'cli' | 'typescript' | 'curl'
export const LANGS: { id: Lang; label: string }[] = [
  { id: 'cli', label: 'paperwork CLI' },
  { id: 'typescript', label: 'TypeScript' },
  { id: 'curl', label: 'curl' },
]

export interface SnippetInput {
  stop: Stop
  params: DemoParams[keyof DemoParams]
  /** Public sample URL when a sample is selected; undefined for an own upload. */
  sampleUrl?: string
  /** The own file's name, for the `--file ./name` form. */
  fileName?: string
  templateUrl?: string
}

type Body = Record<string, unknown>
const j = (v: unknown) => JSON.stringify(v, null, 2) ?? 'null'
const indent = (text: string, pad: string) => text.split('\n').join(`\n${pad}`)
/** Single-quoted shell literal; a schema description can hold an apostrophe. */
const sq = (s: string) => `'${String(s ?? '').replace(/'/g, `'\\''`)}'`

/** The /v1 body as the API takes it. `file` is a url for samples, an id for uploads. */
export function buildBody({ stop, params, sampleUrl, templateUrl }: SnippetInput): Body {
  const p = params as Record<string, unknown>
  const fileRef = sampleUrl ? { url: sampleUrl } : { id: 'file_…' }
  const file = (processing?: string) => ({
    file: processing ? { ...fileRef, processing } : fileRef,
  })
  switch (stop.demo) {
    case 'parse-md':
      return file(typeof p.processing === 'string' ? p.processing : undefined)
    case 'parse-ocr':
      return file('ocr')
    case 'transcribe':
      return file('transcribe_diarize')
    case 'extract-invoice':
      return { ...file(), schema: p.schema, citations: true }
    case 'call-notes':
      return { ...file('transcribe_diarize'), schema: p.schema, citations: true }
    case 'redact':
      return { ...file(), categories: p.categories ?? REDACT_CATEGORIES, mode: 'targeted' }
    case 'compose-invoice':
      return { template: 'playground-invoice', data: p.data, output: 'raw' }
    case 'fill-form':
      return {
        template: { url: templateUrl ?? 'https://…/certificate.pdf' },
        values: p.values,
        output: 'flattened',
      }
  }
}

const QUERY: Record<Stop['verb'], string> = {
  parse: '?wait=120&output=inline',
  extract: '?wait=120&output=inline',
  redact: '?wait=120',
  fill: '?wait=120',
  compose: '',
}

export function snippets(input: SnippetInput): Record<Lang, string> {
  const { stop, sampleUrl, fileName } = input
  const body = buildBody(input)
  const own = !sampleUrl && stop.kind !== 'none'
  const localFile = `./${fileName ?? (stop.kind === 'audio' ? 'call.mp3' : 'document.pdf')}`
  return {
    cli: cli(stop, body, own, localFile),
    typescript: ts(stop, body, own, localFile),
    curl: curl(stop, body, own, localFile),
  }
}

// --- paperwork CLI -------------------------------------------------------------
// `--output`/`--schema` are rewritten to `--output-param`/`--schema-param` by the
// CLI (custom.rs); emit the -param forms so the snippet survives CLI versions.
// `--json` cannot be combined with per-field flags, so the file always goes in
// `--file` (a URL or a JSON file object for samples, a path or an id for uploads).
// Verified with `paperwork 0.0.4 --dry-run`.
function cli(stop: Stop, body: Body, own: boolean, localFile: string): string {
  const p = body as Record<string, unknown>
  const fileObj = p.file as Body | undefined
  const processing = fileObj?.processing as string | undefined
  const lines: string[] = ['export PAPERWORK_TOKEN=sk_…', '']
  // A processing value must ride on the upload: `--params '{"file":…}'` would
  // replace the uploaded id. `files upload` returns the id to run on.
  let src: string
  if (own && processing) {
    lines.push(
      `FILE_ID=$(paperwork files upload ${localFile} --processing ${processing} --format json | jq -r .id)`,
    )
    src = `--file "$FILE_ID"`
  } else if (own) src = `--file ${localFile}`
  else src = `--file ${sq(processing ? j(fileObj) : String(fileObj?.url))}`
  switch (stop.demo) {
    case 'parse-md':
    case 'parse-ocr':
    case 'transcribe':
      lines.push(`paperwork parse ${src} --output-param inline`)
      break
    case 'extract-invoice':
    case 'call-notes':
      lines.push(
        `paperwork extract ${src} \\`,
        `  --schema-param ${sq(j(p.schema))} \\`,
        `  --params '{"citations":true}'`,
      )
      break
    case 'redact':
      lines.push(
        `paperwork redact ${src} \\`,
        `  ${(p.categories as string[]).map((c) => `--categories ${sq(c)}`).join(' ')} \\`,
        `  --mode targeted`,
      )
      break
    case 'compose-invoice':
      lines.push(
        `paperwork compose create --template playground-invoice \\`,
        `  --data ${sq(j(p.data))} \\`,
        `  --output-param raw -o invoice.pdf`,
      )
      break
    case 'fill-form': {
      lines.push(`paperwork fill --json ${sq(j(p))}`)
      break
    }
  }
  return lines.join('\n')
}

// --- TypeScript (@cloudraker/api) -----------------------------------------------
function ts(stop: Stop, body: Body, own: boolean, localFile: string): string {
  const head = [
    `import { CloudRakerClient } from "@cloudraker/api";`,
    ...(own ? [`import { readFile } from "node:fs/promises";`] : []),
    '',
    `const client = new CloudRakerClient({ token: process.env.CLOUDRAKER_API_KEY! });`,
    '',
  ]
  const upload = own
    ? [
        `// 1. Register the file, then PUT the bytes to the signed upload URL.`,
        `const mimeType = "${stop.kind === 'audio' ? 'audio/mpeg' : 'application/pdf'}";`,
        `const file = await client.files.createFile({ name: "${localFile.slice(2)}", mimeType${processingOf(body) ? `, processing: "${processingOf(body)}"` : ''} });`,
        `await fetch(file.uploadUrl!, { method: "PUT", headers: { "Content-Type": mimeType }, body: await readFile("${localFile}") });`,
        '',
        `// 2. Run the verb on the file id.`,
      ]
    : []
  const b = own ? withId(body) : body
  let call: string
  switch (stop.verb) {
    case 'parse':
      call = `const run = await client.parse({ output: "inline", wait: 120, body: ${indent(jt(b), '')} });`
      break
    case 'extract':
      call = `const run = await client.extract({ output: "inline", wait: 120, body: ${indent(jt(b), '')} });`
      break
    case 'redact':
      call = `const run = await client.redact({ wait: 120, body: ${indent(jt(b), '')} });`
      break
    case 'fill':
      call = `const run = await client.fill({ wait: 120, body: ${indent(jt(b), '')} });`
      break
    case 'compose':
      call = `const pdf = await client.compose.create(${indent(jt(b), '')});\n// pdf is the binary response — write it to disk.`
      break
  }
  const tail = stop.verb === 'compose' ? [] : ['', outputLine(stop)]
  return [...head, ...upload, call, ...tail].join('\n')
}

/** `file.id` is an identifier in TypeScript, not a string. */
const jt = (v: unknown) => String(j(v) ?? '').replace('"file.id"', 'file.id')
const processingOf = (body: Body) =>
  (body.file as Body | undefined)?.processing as string | undefined
const withId = (body: Body, id = 'file.id'): Body => {
  const f = body.file as Body | undefined
  const files = body.files as Body[] | undefined
  return {
    ...body,
    ...(f ? { file: { id, ...(f.processing ? { processing: f.processing } : {}) } } : {}),
    ...(files ? { files: [{ id }] } : {}),
  }
}
const outputLine = (stop: Stop) =>
  ({
    parse:
      stop.kind === 'audio'
        ? 'console.log(run.output?.json); // { language, segments[{start,end,text,speaker}] }'
        : 'console.log(run.output?.markdown);',
    extract: 'console.log(run.output?.value, run.output?.citations);',
    redact: 'console.log(run.output?.file?.url, run.output?.entities);',
    fill: 'console.log(run.output?.file?.url, run.output?.fields);',
    compose: '',
  })[stop.verb]

// --- curl ----------------------------------------------------------------------
function curl(stop: Stop, body: Body, own: boolean, localFile: string): string {
  const auth = `-H "Authorization: Bearer $CLOUDRAKER_API_KEY"`
  // Own-file bodies reference $FILE_ID, so their heredoc stays unquoted (expands).
  const post = (path: string, data: Body, extra = '', quote = "'") =>
    [
      `curl -sS -X POST "${API}${path}" \\`,
      `  ${auth} \\`,
      `  -H "Content-Type: application/json" \\`,
      `  -d @- <<${quote}JSON${quote}${extra}`,
      j(data),
      'JSON',
    ].join('\n')
  const lines: string[] = []
  if (own) {
    const mime = stop.kind === 'audio' ? 'audio/mpeg' : 'application/pdf'
    lines.push(
      '# 1. Register the file; the reply carries `id` and a signed `uploadUrl`.',
      `FILE=$(${post('/v1/files', {
        name: localFile.slice(2),
        mimeType: mime,
        ...(processingOf(body) ? { processing: processingOf(body) } : {}),
      })}\n)`,
      'FILE_ID=$(echo "$FILE" | jq -r .id)',
      'UPLOAD_URL=$(echo "$FILE" | jq -r .uploadUrl)',
      '',
      '# 2. PUT the bytes (Content-Type must equal mimeType).',
      `curl -X PUT "$UPLOAD_URL" -H "Content-Type: ${mime}" --data-binary @${localFile}`,
      '',
      '# 3. Run the verb on the file id.',
    )
  }
  const path = stop.verb === 'compose' ? '/v1/compose' : `/v1/${stop.verb}${QUERY[stop.verb]}`
  lines.push(
    post(
      path,
      own ? withId(body, '$FILE_ID') : body,
      stop.verb === 'compose' ? ' -o invoice.pdf' : '',
      own ? '' : "'",
    ),
  )
  return lines.join('\n')
}
